// src/hooks/useSession.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchMessagesWithToken, persistMessageWithToken } from '../api/ai';

const STORAGE_KEY = 'ai_session_id';

export function useSession(getToken) {
  // ✅ همیشه یک UUID کاملاً جدید بسازید – سشن قبلی را ادامه ندهید
  const getInitialSessionId = () => {
    const newId = crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}`;
    // ذخیره در هر دو storage برای استفاده‌های جانبی (مثلاً ChatSidebar)
    sessionStorage.setItem(STORAGE_KEY, newId);
    localStorage.setItem(STORAGE_KEY, newId);
    return newId;
  };

  const [sessionId, setSessionIdState] = useState(getInitialSessionId);
  const [sessionFull, setSessionFull] = useState(false);

  // ۲. استفاده از ref برای جلوگیری از stale closure در persistMessage
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
    console.log('🔄 SESSION ID UPDATED:', sessionId);
  }, [sessionId]);

  // ۳. تابع به‌روزرسانی که هم state و هم storage را آپدیت می‌کند
  const updateSessionId = useCallback((newId) => {
    sessionStorage.setItem(STORAGE_KEY, newId);
    localStorage.setItem(STORAGE_KEY, newId);
    sessionIdRef.current = newId;
    setSessionIdState(newId);
  }, []);

  // ۴. شروع یک سشن کاملاً جدید (با کلیک کاربر روی «گفتگوی جدید»)
  const startNewSession = useCallback(() => {
    const newId = crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}`;
    updateSessionId(newId);
    setSessionFull(false);
    return newId;
  }, [updateSessionId]);

  // ۵. بارگذاری پیام‌های یک سشن خاص
  const loadSessionMessages = useCallback(async (sid) => {
    const token = getToken();
    if (!token) return [];
    try {
      const data = await fetchMessagesWithToken(token, sid);
      return (Array.isArray(data) ? data : []).map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        thinkingSteps: [],
        cards: null,
      }));
    } catch {
      return [];
    }
  }, [getToken]);

  // ۶. ذخیره پیام در سرور – از ref استفاده می‌کند تا همیشه آخرین sessionId را داشته باشد
  const persistMessage = useCallback(async (role, content) => {
    const token = getToken();
    if (!token) return;
    const currentId = sessionIdRef.current;
    console.log('💾 Persisting message with sessionId:', currentId);
    try {
      await persistMessageWithToken(token, { session_id: currentId, role, content });
    } catch (e) {
      console.error('Persist error', e);
      throw e;
    }
  }, [getToken]);   // فقط به getToken وابسته است

  return {
    sessionId,
    setSessionId: updateSessionId,
    sessionFull,
    setSessionFull,
    startNewSession,
    loadSessionMessages,
    persistMessage,
  };
}